import React from 'react';
import { replaceStrValues } from 'platform/shared/health-apps';
import {
  currencyUI,
  currencySchema,
  radioUI,
  checkboxUI,
  radioSchema,
  checkboxSchema,
  textUI,
  numberUI,
  numberSchema,
  titleUI,
  arrayBuilderItemFirstPageTitleUI,
  arrayBuilderItemSubsequentPageTitleUI,
  currentOrPastDateRangeUI,
  currentOrPastDateRangeSchema,
  arrayBuilderYesNoSchema,
  arrayBuilderYesNoUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import { arrayBuilderPages } from '~/platform/forms-system/src/js/patterns/array-builder';
import { customTextSchema } from '../../../definitions';
import {
  careRecipientLabels,
  allCareTypeLabels,
  getCareTypeLabels,
  careFrequencyLabels,
} from '../../../../utils/labels';
import {
  transformDate,
  formatCurrency,
  shouldSkipExpensePages,
} from '../../../../utils/helpers';
import content from '../../../../utils/content.json';

function introDescription() {
  return (
    <div>
      <p className="vads-u-margin-top--0">
        Next we’ll ask you about unreimbursed recurring care expenses that you
        or your dependents pay for. You may add up to 3 care expenses.
      </p>
      <p>
        Examples of unreimbursed care expenses include payments to in-home care
        providers, nursing homes, or other care facilities that insurance won’t
        cover.
      </p>
      <p>
        <span className="vads-u-font-weight--bold">Note: </span>
        We use your expenses to lower the income we count toward your claim. If
        you have no income to report, you do not need to list any expenses.
      </p>

      <va-additional-info trigger="Additional documents we may ask for">
        <p>
          If you’re reporting in-home care, nursing home, or other care facility
          expenses, you may need to submit proof for these expenses with your
          form.
        </p>
        <p>
          You may also need to submit 1 or more of these VA forms signed by a
          provider:
        </p>
        <ul>
          <li className="vads-u-margin-bottom--2">
            Worksheet for a Residential Care, Adult Daycare, or Similar Facility
            from the PDF version of VA Form 21P-534EZ
            <span className="vads-u-display--block">
              <va-link
                href="https://www.va.gov/forms/21p-534ez/"
                text="Get VA Form 21P-534EZ to download"
                external
              />
            </span>
          </li>
          <li className="vads-u-margin-bottom--2">
            Worksheet for In-Home Attendant from the PDF version of VA Form
            21P-534EZ
            <span className="vads-u-display--block">
              <va-link
                href="https://www.va.gov/forms/21p-534ez/"
                text="Get VA Form 21P-534EZ to download"
                external
              />
            </span>
          </li>
          <li className="vads-u-margin-bottom--2">
            Request for Nursing Home Information in Connection with Claim for
            Aid and Attendance (VA Form 21-0779)
            <span className="vads-u-display--block vads-u-margin-bottom--2">
              <va-link
                href="https://www.va.gov/supporting-forms-for-claims/submit-nursing-home-information-form-21-0779"
                text="Submit request for nursing home information in connection with claim for Aid and Attendance"
                external
              />
            </span>
            <span className="vads-u-display--block">
              <va-link
                href="https://www.va.gov/find-forms/about-form-21-0779/"
                text="Get VA Form 21-0779 to download"
                external
              />
            </span>
          </li>
          <li>
            Examination for Housebound Status or Permanent Need for Regular Aid
            and Attendance form (VA Form 21-2680)
            <span className="vads-u-display--block vads-u-margin-bottom--2">
              <va-link
                href="https://www.va.gov/pension/aid-attendance-housebound/apply-form-21-2680"
                text="Apply for Aid and Attendance benefits or Housebound allowance"
                external
              />
            </span>
            <span className="vads-u-display--block">
              <va-link
                href="https://www.va.gov/find-forms/about-form-21-2680/"
                text="Get VA Form 21-2680 to download"
                external
              />
            </span>
          </li>
        </ul>
        <p>
          You can submit VA Form 21-0779 online. All documents and worksheets
          can be uploaded at the end of this application or sent to us by mail.
        </p>
      </va-additional-info>
    </div>
  );
}

/** @type {ArrayBuilderOptions} */
export const options = {
  arrayPath: 'careExpenses',
  nounSingular: content['financial-care-noun-singular'],
  nounPlural: content['financial-care-noun-plural'],
  required: false,
  maxItems: 3,
  isItemIncomplete: item =>
    !item?.careType ||
    !item?.recipient ||
    !item?.provider ||
    !item?.careDateRange?.from ||
    !item?.paymentAmount,
  text: {
    cancelAddTitle: replaceStrValues(
      content['array-cancel-add-title'],
      `${content['financial-care-noun-singular']}?`,
    ),
    cancelEditTitle: replaceStrValues(
      content['array-cancel-edit-title'],
      `${content['financial-care-noun-singular']}?`,
    ),
    cancelAddDescription: content['financial-care-cancel-add-description'],
    cancelEditDescription: content['financial-care-cancel-edit-description'],
    cancelAddYes: content['array-cancel-add-yes'],
    cancelAddNo: content['array-cancel-add-no'],
    cancelEditYes: content['array-cancel-edit-yes'],
    cancelEditNo: content['array-cancel-edit-no'],
    deleteDescription: replaceStrValues(content['array-delete-description'], [
      `${content['financial-care-noun-plural']}.`,
      `${content['financial-care-noun-singular']}.`,
    ]),
    deleteNo: content['array-delete-no'],
    deleteTitle: replaceStrValues(
      content['array-delete-title'],
      `${content['financial-care-noun-singular']}?`,
    ),
    deleteYes: content['array-delete-yes'],
    alertMaxItems: (
      <div>
        <p className="vads-u-margin-top--0">
          You have added the maximum number of allowed care expenses for this
          application. Additional care expenses can be added using a Medical
          Expense Report (VA Form 21P-8416).
        </p>
        <p>
          You can submit this form online, upload it at the end of this
          application, or send it to us by mail.
        </p>
        <span className="vads-u-display--block vads-u-margin-bottom--2">
          <va-link
            href="https://www.va.gov/supporting-forms-for-claims/submit-medical-expense-report-form-21p-8416"
            text="Submit medical expenses to support a pension or parents’ D.I.C. claim"
            external
          />
        </span>
        <span className="vads-u-display--block">
          <va-link
            href="https://www.va.gov/find-forms/about-form-21p-8416"
            external
            text="Get VA Form 21P-8416 to download"
          />
        </span>
      </div>
    ),
    getItemName: (item, index) =>
      `Expense ${index + 1}: ${item?.provider || 'Care provider'}`,
    cardDescription: item => {
      const hasStartDate = !!item?.careDateRange?.from;
      const hasEndDate = !!item?.careDateRange?.to;
      let dateLabel = null;

      if (hasStartDate && hasEndDate) {
        dateLabel = `${transformDate(
          item.careDateRange.from,
        )} - ${transformDate(item.careDateRange.to)}`;
      } else if (hasStartDate) {
        dateLabel = transformDate(item.careDateRange.from);
      }

      const frequencyLabel = careFrequencyLabels[item?.paymentFrequency];
      const amountLabel = formatCurrency(item?.paymentAmount);

      return (
        <div>
          {dateLabel && (
            <span className="vads-u-display--block">{dateLabel}</span>
          )}
          {frequencyLabel && (
            <span className="vads-u-display--block">{frequencyLabel}</span>
          )}
          {amountLabel && (
            <span className="vads-u-display--block">{amountLabel}</span>
          )}
        </div>
      );
    },
    summaryTitle: content['financial-care-summary-title'],
    yesNoBlankReviewQuestion: content['financial-care-yes-no-blank'],
  },
};

const introPage = {
  uiSchema: {
    ...titleUI({
      title: content['financial-care-intro-title'],
      nounSingular: options.nounSingular,
      nounPlural: options.nounPlural,
    }),
    'ui:description': introDescription,
  },
  schema: {
    type: 'object',
    properties: {},
  },
};

const summaryPage = {
  uiSchema: {
    'view:careExpensesList': arrayBuilderYesNoUI(
      options,
      { hint: '' },
      { hint: '' },
    ),
  },
  schema: {
    type: 'object',
    properties: {
      'view:careExpensesList': arrayBuilderYesNoSchema,
    },
    required: ['view:careExpensesList'],
  },
};

const typeOfCarePage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: content['financial-care-type-title'],
      nounSingular: options.nounSingular,
      nounPlural: options.nounPlural,
    }),
    careType: radioUI({
      title: content['financial-care-type-question'],
      labels: allCareTypeLabels,
      updateSchema: (formData, schema, uiSchema, index, path, fullData) => ({
        enum: Object.keys(getCareTypeLabels(fullData)),
      }),
    }),
  },
  schema: {
    type: 'object',
    properties: {
      careType: radioSchema(Object.keys(allCareTypeLabels)),
    },
    required: ['careType'],
  },
};

const recipientPage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(
      content['financial-care-recipient-title'],
    ),
    recipient: radioUI({
      title: content['financial-care-recipient-question'],
      labels: careRecipientLabels,
    }),
    recipientName: textUI({
      title: content['financial-care-recipient-name'],
      maxLength: 256,
      expandUnder: 'recipient',
      expandUnderCondition: field => field === 'OTHER',
      required: (formData, index, fullData) => {
        const items = formData?.careExpenses ?? fullData?.careExpenses;
        const item = items?.[index];
        return item?.recipient === 'OTHER';
      },
      errorMessages: {
        required: content['financial-care-recipient-name-error'],
      },
    }),
    provider: textUI({
      title: content['financial-care-provider'],
      errorMessages: {
        required: content['financial-care-provider-error'],
      },
    }),
  },
  schema: {
    type: 'object',
    properties: {
      recipient: radioSchema(Object.keys(careRecipientLabels)),
      recipientName: customTextSchema,
      provider: customTextSchema,
    },
    required: ['recipient', 'provider'],
  },
};

const datePage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(
      content['financial-care-date-title'],
    ),
    careDateRange: currentOrPastDateRangeUI(
      {
        title: content['financial-care-date-start'],
        monthSelect: false,
      },
      {
        title: content['financial-care-date-end'],
        monthSelect: false,
        hideIf: (formData, index, fullData) => {
          const careExpenses = formData?.careExpenses ?? fullData?.careExpenses;
          const careExpense = careExpenses?.[index];
          return careExpense?.noCareEndDate === true;
        },
      },
      content['date-error'],
    ),
    noCareEndDate: checkboxUI(content['financial-care-no-end-date']),
  },
  schema: {
    type: 'object',
    properties: {
      careDateRange: {
        ...currentOrPastDateRangeSchema,
        required: ['from'],
      },
      noCareEndDate: checkboxSchema,
    },
  },
};

const costPage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(
      content['financial-care-cost-title'],
    ),
    paymentFrequency: radioUI({
      title: content['financial-care-cost-frequency'],
      labels: careFrequencyLabels,
    }),
    paymentAmount: currencyUI({
      title: content['financial-care-cost-payment'],
      max: 999999999,
    }),
    ratePerHour: {
      ...currencyUI({
        title: content['financial-care-cost-rate'],
        max: 99999,
        hideIf: (formData, index, fullData) => {
          const careExpenses = formData?.careExpenses ?? fullData?.careExpenses;
          const careExpense = careExpenses?.[index];
          return careExpense?.careType !== 'IN_HOME_CARE_ATTENDANT';
        },
      }),
      'ui:required': (formData, index, fullData) => {
        const careExpenses = formData?.careExpenses ?? fullData?.careExpenses;
        const careExpense = careExpenses?.[index];
        return careExpense?.careType === 'IN_HOME_CARE_ATTENDANT';
      },
    },
    hoursPerWeek: {
      ...numberUI({
        title: content['financial-care-cost-hours'],
        max: 999,
        errorMessages: {
          required: content['number-error'],
        },
        hideIf: (formData, index, fullData) => {
          const careExpenses = formData?.careExpenses ?? fullData?.careExpenses;
          const careExpense = careExpenses?.[index];
          return careExpense?.careType !== 'IN_HOME_CARE_ATTENDANT';
        },
      }),
      'ui:required': (formData, index, fullData) => {
        const careExpenses = formData?.careExpenses ?? fullData?.careExpenses;
        const careExpense = careExpenses?.[index];
        return careExpense?.careType === 'IN_HOME_CARE_ATTENDANT';
      },
    },
  },
  schema: {
    type: 'object',
    properties: {
      paymentFrequency: radioSchema(Object.keys(careFrequencyLabels)),
      paymentAmount: currencySchema,
      ratePerHour: currencySchema,
      hoursPerWeek: numberSchema,
    },
    required: ['paymentAmount', 'paymentFrequency'],
  },
};

// Should show when the following are true:
// 2022: Always show.
// 2025: If survivors pension is not checked, show pages.
// 2025: If survivors pension is checked, only show pages if incomeSourcesCount is not 'NO_INCOME'.
export const careExpensesPages = arrayBuilderPages(options, pageBuilder => ({
  careExpensesIntro: pageBuilder.introPage({
    title: content['financial-care-intro-title'],
    path: 'financial-information/care-expenses',
    depends: formData => !shouldSkipExpensePages(formData),
    uiSchema: introPage.uiSchema,
    schema: introPage.schema,
  }),
  careExpensesSummary: pageBuilder.summaryPage({
    title: content['financial-care-summary-title'],
    path: 'financial-information/care-expenses/add',
    depends: formData => !shouldSkipExpensePages(formData),
    uiSchema: summaryPage.uiSchema,
    schema: summaryPage.schema,
  }),
  careTypePage: pageBuilder.itemPage({
    title: content['financial-care-type-title'],
    path: 'financial-information/care-expenses/:index/type-of-care',
    depends: formData => !shouldSkipExpensePages(formData),
    uiSchema: typeOfCarePage.uiSchema,
    schema: typeOfCarePage.schema,
  }),
  careRecipientPage: pageBuilder.itemPage({
    title: content['financial-care-recipient-title'],
    path: 'financial-information/care-expenses/:index/recipient-provider',
    depends: formData => !shouldSkipExpensePages(formData),
    uiSchema: recipientPage.uiSchema,
    schema: recipientPage.schema,
  }),
  careDatesPage: pageBuilder.itemPage({
    title: content['financial-care-date-title'],
    path: 'financial-information/care-expenses/:index/dates',
    depends: formData => !shouldSkipExpensePages(formData),
    uiSchema: datePage.uiSchema,
    schema: datePage.schema,
  }),
  careCostPage: pageBuilder.itemPage({
    title: content['financial-care-cost-title'],
    path: 'financial-information/care-expenses/:index/cost',
    depends: formData => !shouldSkipExpensePages(formData),
    uiSchema: costPage.uiSchema,
    schema: costPage.schema,
  }),
}));

export default careExpensesPages;
